import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Mail, ShieldCheck, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [approvalId, setApprovalId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const navigate = useNavigate();

  const clearPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  useEffect(() => {
    if (step !== "approval" || !approvalId) return;
    const finalize = async () => {
      try {
        const r = await axios.post(`${API}/admin/complete-login/${approvalId}`, {}, { withCredentials: true });
        if (r.data?.success) {
          clearPoll();
          toast.success("Accès autorisé");
          navigate("/admin/dashboard");
        }
      } catch (e) { /* still pending */ }
    };
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/admin/login-approval-status/${approvalId}`);
        if (data.status === "approved") {
          finalize();
        } else if (data.status === "denied") {
          clearPoll(); setStep("email"); setApprovalId(null);
          toast.error("Connexion refusée depuis l'e-mail.");
        } else if (data.status === "expired") {
          clearPoll(); setStep("email"); setApprovalId(null);
          toast.error("La demande a expiré. Merci de vous reconnecter.");
        }
      } catch (e) { /* ignore transient */ }
    }, 3000);
    return clearPoll;
  }, [step, approvalId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email }, { withCredentials: true });
      if (res.data.requires_approval) {
        setApprovalId(res.data.approval_id);
        setStep("approval");
        toast.success("Un e-mail de confirmation a été envoyé");
      }
    } catch (e) {
      const errorMessage = e.response?.data?.detail || "Impossible d'envoyer le lien de connexion";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4">
            {step === "email" ? <Mail className="w-8 h-8 text-white" /> : <ShieldCheck className="w-8 h-8 text-white" />}
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Administration</h1>
          <p className="text-slate-600 mt-2">
            {step === "email" ? "Connexion par e-mail sécurisée" : "Confirmez la connexion depuis votre e-mail"}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100">
          {step === "email" ? (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="adminEmail" className="text-slate-700 mb-2 block">
                  Adresse e-mail administrateur
                </Label>
                <Input
                  id="adminEmail"
                  type="email"
                  placeholder="vous@exemple.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="admin-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading || !email}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-6"
                data-testid="admin-login-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Envoi du lien...
                  </>
                ) : (
                  "Recevoir le lien de connexion"
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-6" data-testid="admin-approval-step">
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-4 text-sm text-slate-600 flex items-start gap-3">
                <MailCheck className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  Un e-mail de confirmation vous a été envoyé. Ouvrez-le et cliquez sur
                  <strong> « Oui, c'est moi »</strong> pour terminer la connexion. Cette page vous
                  connectera automatiquement.
                </span>
              </div>

              <div className="flex items-center justify-center gap-2 text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">En attente de votre confirmation…</span>
              </div>

              <button
                type="button"
                onClick={() => { clearPoll(); setStep("email"); setApprovalId(null); setError(""); }}
                className="text-slate-500 hover:text-slate-700 text-sm w-full text-center"
                data-testid="admin-approval-back-button"
              >
                ← Retour
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          <a href="/" className="hover:text-slate-700">← Retour au site</a>
        </p>
      </div>
    </div>
  );
}
